/**
 * Widget picker modal — opens from the Dashboard header "Customize"
 * button. Shows a checkbox for each widget; on Save, persists to
 * localStorage and the dashboard re-renders with the new layout.
 *
 * Built on the shared <Modal> primitive rather than a hand-rolled overlay
 * (docs/frontend-standards.md §5). The hand-rolled version had two visible
 * faults the primitive already solves:
 *
 *   · It painted itself on --surface, the RAISED CARD token. On the dark
 *     plane that is #161b26 against a #0e121b page, and its only edge was a
 *     black drop shadow — invisible on a near-black background. The dialog
 *     read as transparent even though it was fully opaque. --surface-elevated
 *     (#1f2634) is the token meant for popovers and modals, and .ui-modal
 *     pairs it with a real border and the --e-4 elevation.
 *   · It rendered inline, so its position:fixed scrim resolved against the
 *     dashboard's containing block and dimmed only the content column,
 *     leaving the topbar and sidebar undimmed. <Modal> portals to <body>.
 *
 * Escape-to-close, scrim-click, body-scroll lock and focus handling all come
 * from the primitive too, so they are no longer this file's problem.
 */
import { useEffect, useState } from 'react';
import { Modal, Button, Icon } from './ui';
import {
  DASHBOARD_WIDGETS,
  WidgetId,
  readWidgetPrefs,
  writeWidgetPrefs,
  resetWidgetPrefs,
} from '../lib/dashboardPrefs';

interface Props {
  userId: string | null;
  open: boolean;
  onClose: () => void;
  onSaved: (prefs: Record<WidgetId, boolean>) => void;
}

export default function DashboardCustomizeModal({ userId, open, onClose, onSaved }: Props) {
  const [prefs, setPrefs] = useState<Record<WidgetId, boolean>>(() => readWidgetPrefs(userId));

  // Re-read on open in case user changed them from another session / tab
  useEffect(() => {
    if (open) setPrefs(readWidgetPrefs(userId));
  }, [open, userId]);

  const toggle = (id: WidgetId) => {
    setPrefs(p => ({ ...p, [id]: !p[id] }));
  };

  const save = () => {
    writeWidgetPrefs(userId, prefs);
    onSaved(prefs);
    onClose();
  };

  const reset = () => {
    resetWidgetPrefs(userId);
    setPrefs(readWidgetPrefs(userId));
  };

  const countOn = Object.values(prefs).filter(Boolean).length;

  return (
    <Modal open={open} onClose={onClose} title="Customize dashboard">
      <p style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
        Toggle widgets on or off. Your choices are saved per browser.
      </p>

      {/* Negative inline margin lets each row's divider run the full width of
          the modal body, the way the old full-bleed list did. */}
      <div style={{ margin: '0 calc(-1 * var(--sp-5))' }}>
        {DASHBOARD_WIDGETS.map(w => {
          const on = prefs[w.id];
          return (
            <label
              key={w.id}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)', padding: 'var(--sp-3) var(--sp-5)', cursor: 'pointer', borderBottom: '1px solid var(--surface-subtle)' }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(w.id)}
                style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--ai)' }}
              />
              {/* Dimmed when the widget is off, so the list reads at a glance.
                  currentColor means this tracks the theme — an emoji could not. */}
              <Icon
                name={w.icon}
                size={17}
                style={{ marginTop: 2, flexShrink: 0, color: on ? 'var(--ai)' : 'var(--text-muted)' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-strong)' }}>{w.label}</div>
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginTop: 2 }}>{w.description}</div>
              </div>
            </label>
          );
        })}
      </div>

      <Modal.Footer>
        <button
          type="button"
          onClick={reset}
          style={{ marginRight: 'auto', padding: 'var(--sp-2) 0', background: 'transparent', color: 'var(--text-muted)', border: 'none', fontSize: 'var(--fs-sm)', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Reset to defaults
        </button>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', alignSelf: 'center' }}>
          {countOn} of {DASHBOARD_WIDGETS.length} on
        </span>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
}
